// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import { BaseToolbar, ConfirmModal, Loading, UnderDevelopment } from '~/components/common';
import { NoteItem } from '~/components/module/note';
import { FormNoteTimeClock } from '~/components/module/timeClock';
// Others
import { INote, IQueryGetListNote } from '~/utils/interface/note';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { LoadingContext } from '~/context';
import { deleteNote, getListNote } from '~/thunks/notes/notesThunk';
import { notesActions, selectIsRefreshNote } from '~/thunks/notes/notesSlice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { icons } from '~/assets';
import { StorageEnum } from '~/utils/enum/base';
import { LoadingSizeEnum, ParamsBusinessTypeEnum, RoleEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './TimeClockNote.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const TimeClockNote = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/timeClock']);
  const { id: timeClockId } = useParams();
  const dispatch = useAppDispatch();
  const loadingContext = useContext(LoadingContext);

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const currentAccountId: string = localStorage.getItem(StorageEnum.ACCOUNT_ID) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshNote = useSelector(selectIsRefreshNote);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [hasMoreNote, setHasMoreNote] = useState<boolean>(false);
  const [currentPageNote, setCurrentPageNote] = useState<number>(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);

  const [listNotes, setListNotes] = useState<INote[]>([]);
  const [noteSelected, setNoteSelected] = useState<INote | null>(null);
  const [isShowFormNote, setIsShowFormNote] = useState<boolean>(false);
  const [isShowConfirmDeleteNote, setIsShowConfirmDeleteNote] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!timeClockId) return;

    const params: IQueryGetListNote = {
      page: currentPageNote,
      limit: DEFAULT_LIMIT_PAGE,
      businessId: timeClockId,
      businessType: ParamsBusinessTypeEnum.TIME_CLOCK,
    };

    handleGetListNote(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeClockId, currentPageNote]);

  useEffect(() => {
    if (!isRefreshNote || !timeClockId) return;

    const params: IQueryGetListNote = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      businessId: timeClockId,
      businessType: ParamsBusinessTypeEnum.TIME_CLOCK,
    };

    handleGetListNote(params);
    dispatch(notesActions.setIsRefreshNote(false));

    setCurrentPageNote(BASE_PAGINATION_DEFAULT_CURRENT_PAGE);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshNote]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListNote = (params: IQueryGetListNote) => {
    if (currentPageNote === BASE_PAGINATION_DEFAULT_CURRENT_PAGE && !isRefreshNote) {
      setIsLoading(true);
    }

    dispatch(getListNote(params))
      .unwrap()
      .then((res) => {
        const data = res?.data;

        if (!data || !data?.responses) {
          setHasMoreNote(false);

          return;
        }

        if (data?.pagination?.page === BASE_PAGINATION_DEFAULT_CURRENT_PAGE) {
          setListNotes(data?.responses ?? []);
          setHasMoreNote(data?.pagination?.totalPages > BASE_PAGINATION_DEFAULT_CURRENT_PAGE);
        } else {
          setListNotes((prevState) => {
            const newData = data?.responses?.filter(
              (note) => !prevState?.some((oldNote) => oldNote?.id === note?.id)
            );

            const updateListNote = [...prevState, ...newData];

            setHasMoreNote(data?.pagination?.totalPages > BASE_PAGINATION_DEFAULT_CURRENT_PAGE);

            return updateListNote;
          });
        }
      })
      .catch((error) => {
        setHasMoreNote(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLoadMoreNote = () => {
    setCurrentPageNote((prev) => prev + DEFAULT_NUMBER_ONE);
  };

  const handleAddNote = () => {
    setIsShowFormNote(true);
  };

  const handleCloseFormNote = () => {
    setIsShowFormNote(false);
    setNoteSelected(null);
  };

  const handleEditNote = (note: INote) => {
    if (!note) return;

    setNoteSelected(note);
    setIsShowFormNote(true);
  };

  const handleDeleteNote = (note: INote) => {
    if (!note) return;

    setNoteSelected(note);
    setIsShowConfirmDeleteNote(true);
  };

  const handleCloseConfirmDelete = () => {
    setIsShowConfirmDeleteNote(false);
    setNoteSelected(null);
  };

  const handleConfirmDelete = () => {
    if (!noteSelected?.id) return;

    loadingContext?.show();

    dispatch(deleteNote(noteSelected?.id))
      .unwrap()
      .then((res) => {
        dispatch(notesActions.setIsRefreshNote(true));
      })
      .catch((error) => {})
      .finally(() => {
        loadingContext?.hide();
        handleCloseConfirmDelete();
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='timeClockNotePage' className={cx('container')}>
      <BaseToolbar textAction={t('time_clocks_btn_add_note')} onAction={handleAddNote}></BaseToolbar>

      {!isLoading && (
        <Box component={'div'} id='scrollableNotes' className={cx('listNoteContainer')}>
          <InfiniteScroll
            dataLength={listNotes.length}
            next={handleLoadMoreNote}
            hasMore={hasMoreNote}
            loader={
              <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
                <CircularProgress size={LoadingSizeEnum.MEDIUM} />
              </Stack>
            }
            scrollableTarget={'mainContentSection'}
            // {...(listNotes?.length > DEFAULT_NUMBER_ZERO &&
            //   hasMoreNote && {
            //     endMessage: listNotes?.length === DEFAULT_NUMBER_ZERO && (
            //       <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} height={35}>
            //         <Typography component={'p'} className={cx('noDataAvailable')}>
            //           {t('common_no_data', { ns: 'common/base' })}
            //         </Typography>
            //       </Stack>
            //     ),
            //   })}
          >
            <Box id='scrollableNotes' component={'div'} className={cx('listNote')}>
              {Array.isArray(listNotes) && listNotes?.length > DEFAULT_NUMBER_ZERO ? (
                listNotes?.map((note) => (
                  <NoteItem
                    note={note}
                    key={note?.id}
                    {...((note?.createdBy === currentAccountId || currentRole === RoleEnum.ADMIN) && {
                      onEdit: handleEditNote,
                      onDelete: handleDeleteNote,
                    })}
                  />
                ))
              ) : (
                <Typography component={'span'} className={cx('noDataAvailable')}>
                  {t('common_no_data_available', { ns: 'common/base' })}
                </Typography>
              )}
            </Box>
          </InfiniteScroll>
        </Box>
      )}

      {isShowFormNote && (
        <FormNoteTimeClock {...(noteSelected && { note: noteSelected })} onClose={handleCloseFormNote} />
      )}

      {isShowConfirmDeleteNote && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete_note', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Box>
  );
};

export default TimeClockNote;
